const memoize = require('./memoizedWorkflows');

const consoleLogger = require('./defaultProviders/logging');
const promiseDispatcher = require('./defaultProviders/dispatch');
const RulesArrayRepository = require('./defaultProviders/RulesArrayRepository');
const BASESTEPS = Object.freeze(require('./defaultProviders/steps'));

const workflowGenerator = require('./workflowGenerator');
const workflowExecutioner = require('./workflowExecutioner');
const { setContextExcludedFields } = require('./contextProvider');

/**
 * @typedef { import("./index").RulesEngine } RulesEngineClass
 * @typedef { import("./index").RulesProvider } RulesProvider
 * @typedef { import("./index").Settings } Settings
 * @typedef { import("./index").Context } Context
 * @typedef { import("./index").Rule } Rule
 */

class RulesEngine {
    /**
     * @constructor
     * @param {Rules[]|RulesProvider} rules
     * @param {Settings} [settings]
     */
    constructor(rules, settings = {}) {
        const {
            memoizer = memoize,
            logging,
            dispatch,
            steps,
            states,
            cacheAge,
            onStep
        } = settings;
        rejectRemovedSettings(settings);
        if (onStep !== undefined && !isFunction(onStep)) {
            throw new Error('`onStep` should be a function.');
        }
        setContextExcludedFields(settings.contextExcludedFields);
        this.STEPS = Object.freeze({ ...BASESTEPS, ...steps });
        this.log = { ...consoleLogger, ...(logging || {}) };
        this.cacheAge = isNumber(cacheAge) ? cacheAge : 5000;

        if (dispatch) {
            if (isFunction(dispatch)) {
                this.dispatch = dispatch;
            } else {
                throw new Error('`dispatch` should be a function.');
            }
        } else {
            this.dispatch = promiseDispatcher(this);
        }

        if (states && Array.isArray(states) && states.length === 2) {
            this.STATES = Object.freeze({ success: states[0], fail: states[1] });
        } else {
            this.STATES = Object.freeze({ success: 'success', fail: 'fail' });
        }
        if (!rules) {
            throw new Error('`rules` should be passed as the first argument to the rulesEngine.io constructor');
        }
        if (Array.isArray(rules)) {
            if (!rules.length) {
                throw new Error('At least 1 rule should be provided.');
            }
            this.rulesRepository = new RulesArrayRepository(rules);
        } else if (rules.find) {
            this.rulesRepository = rules;
        } else {
            throw new Error('`rules` should either be an array, or an object with a `find` method.');
        }

        this.generator = workflowGenerator.prepareGenerator(this.STEPS, this.rulesRepository, this.log);
        this.executioner = workflowExecutioner.prepareEngine(this.dispatch, this.log, {
            states: this.STATES,
            onStep
        });
        this._memoizedWorkflowCreation = memoizer(this.generator.createWorkflow, this.cacheAge);
    }

    /**
     *
     * @param {any} data
     * @param {Rule[] | Context} contextOrWorkflow
     * @param {Context} [context]
     * @returns {Promise<any>}
     */
    async execute(data, contextOrWorkflow, contextOrOptions, maybeOptions) {
        // Which overload this is, decided on the SECOND argument. Deciding on "is the third argument
        // missing" instead would mistake an options object for the context on
        // `execute(data, context, options)`, and skip building the workflow entirely.
        let workflow;
        let context;
        let options;
        if (Array.isArray(contextOrWorkflow)) {
            workflow = contextOrWorkflow;
            context = contextOrOptions;
            options = maybeOptions;
        } else {
            context = contextOrWorkflow;
            options = contextOrOptions;
        }

        // Validated after disambiguation, so the message names the argument the caller actually got
        // wrong rather than whichever position it landed in.
        //
        // The full routing check applies only when the context has to be INFERRED from argument 2.
        // A caller that supplies its own workflow has always been able to pass a narrower context —
        // the workflow is already built, so nothing here needs to route — and demanding
        // namespace/relation of it would reject callers that work today.
        const contextIsUsable = workflow
            ? Boolean(context)
            : Boolean(context && context.verb && context.namespace && context.relation);
        if (!contextIsUsable) {
            throw new Error(
                'A proper `Context` is required. The 3rd argument is missing, and the 2nd argument does not appear to be a context.'
            );
        }
        if (options?.onStep !== undefined && !isFunction(options.onStep)) {
            throw new Error('`onStep` should be a function.');
        }

        if (!workflow) workflow = await this.generator.createWorkflow(context);
        // Forwarded only when the caller supplied something, so the argument list an existing test
        // or spy observes is unchanged for every caller that does not use it.
        return options === undefined
            ? this.executioner.execute(data, workflow, context)
            : this.executioner.execute(data, workflow, context, options);
    }

    /**
     *
     * @param {Context} context
     * @returns {Rule[]}
     */
    async createWorkflow(context) {
        return this._memoizedWorkflowCreation(context);
    }
}

/**
 * Refuse settings that were removed, rather than ignoring them.
 *
 * `enableWorkflowStack` wrote per-step markers onto the compiled workflow, which `createWorkflow`
 * memoizes and every concurrent message shares, and on a skipped step it deleted that step's
 * `prerequisites` — so a later message reusing the cached workflow ran the rule without its
 * lookups. `onStep` reports the same steps without touching the workflow.
 *
 * Matched without regard to case because an ignored setting is exactly how a caller ends up
 * believing a feature is on when it is not: a consumer in this repository passed
 * `enableworkflowStack` for years and never noticed that nothing read it.
 */
function rejectRemovedSettings(settings) {
    // Only a request to turn it ON is refused. `false` asks for what every engine now does anyway,
    // so failing a caller's startup over it would break them for nothing.
    const removed = Object.keys(settings).find(key => key.toLowerCase() === 'enableworkflowstack' && settings[key]);
    if (removed !== undefined) {
        throw new Error(
            `\`${removed}\` was removed in rulesengine.io 3.0.0. Use \`onStep\`, either in the settings or per \`execute\` call, to observe each step without mutating the workflow.`
        );
    }
}

const isNumber = value => typeof value === 'number' && isFinite(value);

const isFunction = value => value && typeof value === 'function';

module.exports = RulesEngine;
